/**
 * exportPayload — the per-difficulty lists the Synth Riders editor sends to `/api/export`.
 *
 * Kept out of `LeftSidebar` so it can be tested without mounting the editor. Export used to send
 * `squares: baked.squares` for every difficulty, which threw away the squares and triangles the
 * mapper placed (Alt+7 / Alt+8) or loaded from the file — SRVR-0073.AC8, CAND-AD. Each difficulty
 * now sends its own obstacles followed by the decor bake.
 */

import type { Difficulty, Obstacle } from '../../types/beatmap';
import type { BakedObstacle, BakedSlide } from '../../types/designShape';

export interface BakedDecor {
  squares: BakedObstacle[];
  triangles: BakedObstacle[];
  slides: BakedSlide[];
}

/** How close an authored obstacle must be to a baked wall to count as the same wall. */
export const DECOR_MATCH_MS = 1;
export const DECOR_MATCH_POS = 0.001;
export const DECOR_MATCH_DEG = 0.001;

/**
 * True when `a` is a baked wall that came back from a re-import as an ordinary obstacle.
 *
 * A baked wall carries no provenance marker in the file, so re-importing a decor map returns it as an
 * authored square or triangle *and* restores the designer group (FMT-0039.AC7). Sending both would
 * write the wall twice, and once more on every round-trip. Two identical obstacles at one place and
 * time are indistinguishable in the game, so dropping the authored copy loses nothing. This is a
 * stopgap for FMT-0041's marker: a re-imported wall the mapper has since moved is not recognised.
 */
function matchesBaked(a: Obstacle, baked: BakedObstacle[]): boolean {
  const p = a.position;
  if (!p) return false;
  return baked.some(b =>
    Math.abs(a.time_ms - b.time_ms) <= DECOR_MATCH_MS
    && Math.abs((p[0] ?? 0) - b.position[0]) <= DECOR_MATCH_POS
    && Math.abs((p[1] ?? 0) - b.position[1]) <= DECOR_MATCH_POS
    && Math.abs((a.z_rotation ?? 0) - b.z_rotation) <= DECOR_MATCH_DEG);
}

function mergeObstacles(authored: Obstacle[] | undefined, baked: BakedObstacle[]): (Obstacle | BakedObstacle)[] {
  const own = (authored ?? []).filter(a => !matchesBaked(a, baked));
  return [...own, ...baked].sort((a, b) => a.time_ms - b.time_ms);
}

/** The `difficulties` object of the export request, one entry per difficulty in the map. */
export function buildExportDifficulties(
  difficulties: Record<string, Difficulty>, baked: BakedDecor,
): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [name, diff] of Object.entries(difficulties)) {
    out[name] = {
      name, notes: diff.notes, slides: diff.slides, crouches: diff.crouches, jumps: diff.jumps,
      crouch_obstacles: diff.crouch_obstacles ?? [],
      squares: mergeObstacles(diff.squares, baked.squares),
      triangles: mergeObstacles(diff.triangles, baked.triangles),
      decor_slides: baked.slides,
    };
  }
  return out;
}
